import * as React from 'react';

import { RecommendationsList } from '../../components/RecommendationsList';
import { RecommendationsListDesktop } from '../../components/RecommendationsListDesktop';
import { useInitRecommendations, useRecommendations } from './useRecommendations';
import { ELoadingStatus } from '../../types/loadingStatus';
import { RecommendationSkeleton } from '../../components/Recommendation';
import { TRecommendation } from '../../types/recommendation';
import { RecommendationDesktop, RecommendationDesktopSkeleton } from '../../components/RecommendationDesktop';
import { FavoriteButtonContainer } from '../FavoriteButtonContainer';
import { LabelsContainer } from '../LabelsContainer';
import { trackEntityTeaserShow } from './tracking';
import { RecommendationsListMobile, RecommendationsPaginationError } from './RecommendationsListMobile';
import { RedesignedRecommendationsListMobile } from '../RedesignedRecommendationsListMobile';
import { RecommendationSkeleton as RedesignedRecommendationSkeleton } from '../../components/RedesignedRecommendation';
import { RecommendationsList as RedesignedRecommendationsList } from '../../components/RedesignedRecommendationsList';
import { RedesignedPaginationError } from '../../components/RedesignedPaginationError';
import { RecommendationsListDesktopWithNewbuildings } from '../../components/RecommendationsListDesktopWithNewbuildings';
import { NewbuildingRecommendations } from '../NewbuildingRecommendations/NewbuildingRecommendations';
import { useSelector } from 'react-redux';
import { selectIsIndexLandingType, selectLandingType } from '../../selectors/landingType';
import { useApplicationContext } from '../../utils/applicationContext';
import { EQueryParameters as ELandingType } from '../../repositories/feed-page/v2/mobile-site/get-recommendations';
import { NEW_MAIN_PAGE_EXPERIMENT } from '../../constants/experiments';
import { Experiment } from '@cian/experiments/ab';

const MOBILE_SKELETON_COUNT = 20;

/**
 * Хелпер выбирает между легаси и редизайн-вариантом для главной в рамках эксперимента.
 * Не main-page брендинг (другие лендинги в группе B) остаётся на легаси-варианте.
 */
type TMainPageExperimentProps = {
  legacy: React.ReactNode;
  redesigned: React.ReactNode;
  isMainPage: boolean;
};

const MainPageExperiment: React.FC<TMainPageExperimentProps> = ({ legacy, redesigned, isMainPage }) => (
  <>
    <Experiment name={NEW_MAIN_PAGE_EXPERIMENT} group="A" default>
      {legacy}
    </Experiment>
    <Experiment name={NEW_MAIN_PAGE_EXPERIMENT} group="B">
      {isMainPage ? redesigned : legacy}
    </Experiment>
  </>
);

export function RecommendationsListContainer() {
  const { config } = useApplicationContext();

  useInitRecommendations();

  const recommendationsControls = useRecommendations();
  const landingType = useSelector(selectLandingType);
  const showRecommendations =
    useSelector(selectIsIndexLandingType) || config.get('NewbuildingRecommendations.ShowOnVerticals.Enabled');
  const isMainPage = landingType === ELandingType.MainPage;

  const infiniteScroll = landingType !== ELandingType.FlatRentForDay;
  const {
    recommendations,
    onRecommendationClick,
    onShow,
    onShowTrap,
    showHeader,
    recommendationsStatus,
    isMobile,
    regionId,
    onMoreClick,
    isOfferVisited,
    showFullList,
    onRetryRecommendations,
  } = recommendationsControls;

  const legacyMobileSkeleton = React.useMemo(
    () =>
      new Array(MOBILE_SKELETON_COUNT)
        .fill(0)
        .map((_, index) => <RecommendationSkeleton key={`recommendation-mobile-skeleton-${index}`} />),
    [],
  );

  const redesignedMobileSkeleton = React.useMemo(
    () =>
      new Array(MOBILE_SKELETON_COUNT)
        .fill(0)
        .map((_, index) => <RedesignedRecommendationSkeleton key={`recommendation-mobile-skeleton-${index}`} />),
    [],
  );

  const DESKTOP_SKELETON_COUNT = showFullList ? 40 : 8;

  const recommendationsDesktopSkeleton = React.useMemo(
    () =>
      new Array(DESKTOP_SKELETON_COUNT)
        .fill(0)
        .map((_, index) => <RecommendationDesktopSkeleton key={`recommendation-desktop-skeleton-${index}`} />),
    [DESKTOP_SKELETON_COUNT],
  );

  const handleEntityTrackTeaserShow = React.useCallback(
    (offerId: number | null, position: number, contextToken?: string) => {
      if (!offerId) {
        return;
      }

      trackEntityTeaserShow({ offerId, position, contextToken }, isMobile);
    },
    [isMobile],
  );

  if (recommendationsStatus === ELoadingStatus.Loading) {
    if (isMobile) {
      return (
        <MainPageExperiment
          isMainPage={isMainPage}
          legacy={<RecommendationsList withHeader={showHeader}>{legacyMobileSkeleton}</RecommendationsList>}
          redesigned={<RedesignedRecommendationsList>{redesignedMobileSkeleton}</RedesignedRecommendationsList>}
        />
      );
    }

    return (
      <RecommendationsListDesktop showMoreLink={!showFullList}>
        {recommendationsDesktopSkeleton}
      </RecommendationsListDesktop>
    );
  }

  if (isMobile && recommendationsStatus === ELoadingStatus.Failure) {
    return (
      <MainPageExperiment
        isMainPage={isMainPage}
        legacy={
          <RecommendationsList withHeader={showHeader}>
            <RecommendationsPaginationError onRetry={onRetryRecommendations} />
          </RecommendationsList>
        }
        redesigned={
          <RedesignedRecommendationsList>
            <RedesignedPaginationError onRetry={onRetryRecommendations} />
          </RedesignedRecommendationsList>
        }
      />
    );
  }

  if (!recommendations.length) {
    return null;
  }

  return isMobile ? (
    <MainPageExperiment
      isMainPage={isMainPage}
      legacy={
        <RecommendationsListMobile
          recommendationsControls={recommendationsControls}
          onTrack={handleEntityTrackTeaserShow}
          infiniteScroll={infiniteScroll}
        />
      }
      redesigned={
        <RedesignedRecommendationsListMobile
          recommendationsControls={recommendationsControls}
          onTrack={handleEntityTrackTeaserShow}
        />
      }
    />
  ) : !showRecommendations ? (
    <RecommendationsListDesktop onShow={onShow} onMoreClick={onMoreClick} showMoreLink={!showFullList}>
      {recommendations.map((recommendation: TRecommendation, index) => (
        <RecommendationDesktop
          onTrack={handleEntityTrackTeaserShow}
          position={index + 1}
          key={recommendation.offerId}
          onClick={onRecommendationClick}
          onShowTrap={onShowTrap}
          regionId={regionId}
          favoriteButton={<FavoriteButtonContainer size="M" id={recommendation.offerId} />}
          labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
          {...recommendation}
        />
      ))}
    </RecommendationsListDesktop>
  ) : (
    <RecommendationsListDesktopWithNewbuildings
      showMoreLink={!showFullList}
      secondRow={<NewbuildingRecommendations />}
      onShow={onShow}
      onMoreClick={onMoreClick}
    >
      {recommendations.map((recommendation: TRecommendation, index) => (
        <RecommendationDesktop
          onTrack={handleEntityTrackTeaserShow}
          position={index + 1}
          key={recommendation.offerId}
          onClick={onRecommendationClick}
          onShowTrap={onShowTrap}
          regionId={regionId}
          favoriteButton={<FavoriteButtonContainer size="M" id={recommendation.offerId} />}
          labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
          {...recommendation}
        />
      ))}
    </RecommendationsListDesktopWithNewbuildings>
  );
}
